import type { BookingSessionResponse } from '@massilia/queries'

import { Skeleton, useIsMobile } from '@massilia/ui'
import {
  Banknote,
  Briefcase,
  CalendarClock,
  ChevronDown,
  CreditCard,
  Landmark,
  Luggage,
  ScrollText,
  Ticket,
} from 'lucide-react'
import { type ReactNode, useMemo } from 'react'

import { StopsIndicator } from '@/components/flights/stops-indicator'
import { formatDuration } from '@/utils/format-duration'
import { formatPrice } from '@/utils/format-price'
import { PASSENGER_TYPE_LABELS } from '@/utils/passenger-labels'
import { JOURNEY_TYPE_LABELS } from '@/utils/trip-labels'

import { type BookingPriceView, toPriceView } from './price-preview'
import { FlightSegmentCard, PassengerCard, TicketConditions } from './shared-trip-components'

/** Moyens de paiement acceptés par l'agence — informatif, cf. les options réelles du checkout */
const PAYMENT_METHODS = [
  { Icon: CreditCard, label: 'Carte bancaire' },
  { Icon: Landmark, label: 'Virement' },
  { Icon: ScrollText, label: 'Chèque' },
  { Icon: Ticket, label: 'Chèques vacances' },
  { Icon: Banknote, label: 'Espèces (en agence)' },
]

const SHOW_RULE_DEBUG =
  import.meta.env.DEV || import.meta.env.VITE_SHOW_CONDITIONS_RULE_DEBUG === 'true'

// ── Props ──

interface BookingSidebarProps {
  /** Contact info to display (checkout only) */
  contactInfo?: {
    email: string
    phone?: string
  }
  /** When true, only the insurance price + total show a skeleton (rest of the recap stays visible) */
  insurancePriceLoading?: boolean
  /** Price Affidium en cours : seules les conditions du billet passent en skeleton */
  isPriceLoading?: boolean
  /** Override insurance price (used on assurances page where selection is dynamic) */
  overrideInsurancePrice?: number
  /**
   * Aperçu provisoire issu de l'offre sélectionnée, affiché tant que le price n'a pas
   * répondu. Écrasé en entier par `session.priceData` dès qu'il arrive.
   */
  preview?: BookingPriceView | null
  /** Promo code discount amount in EUR for total calculation */
  promoCodeDiscount?: null | number
  /** Promo code slot rendered between assurance line and total (checkout only) */
  promoCodeSlot?: ReactNode
  session?: BookingSessionResponse
}

// ── Component ──

export function BookingSidebar({
  contactInfo,
  insurancePriceLoading,
  isPriceLoading,
  overrideInsurancePrice,
  preview,
  promoCodeDiscount,
  promoCodeSlot,
  session,
}: BookingSidebarProps) {
  // Sur mobile le récap est empilé au-dessus du formulaire : les moyens de paiement
  // partent repliés pour ne pas allonger la page si le client ne les cherche pas.
  const isMobile = useIsMobile()
  // Prestataire du dossier (FLOA après un refus Oney), ou le flag backoffice tant que
  // le dossier n'existe pas : le serveur fait le repli. Oney le temps que la session
  // charge, comme le réglage de prod, pour ne pas faire clignoter le libellé.
  const installmentProvider = session?.installmentInfo?.provider ?? 'SSP'
  const paymentMethods = [
    ...PAYMENT_METHODS,
    {
      Icon: CalendarClock,
      label: `${installmentProvider === 'SSP' ? 'Oney' : 'FLOA'} (3x/4x)`,
    },
  ]

  // Le price confirmé remplace l'aperçu en entier : jamais de mélange champ par champ,
  // qui donnerait un récapitulatif mi-provisoire mi-confirmé impossible à relire.
  const priceView = useMemo<BookingPriceView | null>(
    () => (session?.priceData ? toPriceView(session.priceData) : (preview ?? null)),
    [preview, session]
  )

  const passengers = session?.passengers ?? []
  // MOD-INFANT : le frais bébé (10 %) n'est annoncé que si le dossier compte un bébé
  const infantCount =
    priceView?.passengers.find((group) => group.type === 'Infant')?.count ??
    passengers.filter((passenger) => passenger.type === 'Infant').length
  const insurance = session?.insurance
  const withInsurance = insurance?.withInsurance ?? false

  // Price computation
  const flightPriceCents = session?.totalFlightPriceCents ?? priceView?.totalFlightPriceCents ?? 0
  const flightPrice = flightPriceCents / 100

  // Insurance: use override if provided, otherwise use session insurance
  let insurancePrice = 0
  if (overrideInsurancePrice !== undefined) {
    insurancePrice = overrideInsurancePrice
  } else if (withInsurance) {
    insurancePrice = (insurance?.insurancePriceCents ?? 0) / 100
  }
  const totalPrice = flightPrice + insurancePrice
  const effectiveTotal = Math.max(0, totalPrice - (promoCodeDiscount ?? 0))

  // Conditions (en haut) — seule information que l'offre sélectionnée ne porte pas :
  // elle n'existe que dans la réponse price, d'où le skeleton ciblé.
  let ticketConditionsCard: ReactNode = null
  if (priceView?.conditions) {
    ticketConditionsCard = (
      <SidebarCard title="Conditions du billet">
        <TicketConditions
          conditions={priceView.conditions}
          debugRule={SHOW_RULE_DEBUG ? priceView.conditionsRuleDebug : undefined}
          infantCount={infantCount}
        />
      </SidebarCard>
    )
  } else if (isPriceLoading) {
    ticketConditionsCard = (
      <SidebarCard title="Conditions du billet">
        <div className="flex flex-col gap-2">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-4/5" />
        </div>
      </SidebarCard>
    )
  }

  return (
    <div className="sticky top-4 flex flex-col gap-3 rounded-xl border border-gray-light-200 bg-gray-light-50 px-6 py-8">
      {/* Title */}
      <h3 className="text-lg font-semibold text-gray-light-900">Détail du tarif</h3>

      {/* Vol — prix par type passager */}
      <div className="flex flex-col gap-1">
        <p className="text-sm font-semibold text-gray-light-900">Vol</p>
        {priceView?.passengers.map((group, i) => (
          <div className="flex items-center justify-between" key={i}>
            <p className="text-sm font-medium text-gray-light-900">
              {PASSENGER_TYPE_LABELS[group.type as keyof typeof PASSENGER_TYPE_LABELS] ??
                group.type}{' '}
              ({group.count})
            </p>
            <p className="text-sm font-medium text-gray-light-900">
              {formatPrice(Number.parseFloat(group.unitPrice))}
            </p>
          </div>
        ))}
        {/* Fallback if no per-type data but we have a total flight price */}
        {!priceView?.passengers.length && flightPrice > 0 && (
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-gray-light-900">Sous total</p>
            <p className="text-sm font-medium text-gray-light-900">{formatPrice(flightPrice)}</p>
          </div>
        )}
        {/* Ni price confirmé ni aperçu (arrivée directe sur l'URL) : on ne connaît aucun prix */}
        {!priceView && flightPrice === 0 && <Skeleton className="h-4 w-full" />}
      </div>

      {/* Assurance */}
      {(insurancePrice > 0 || insurancePriceLoading) && (
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium text-gray-light-900">Assurance vol</p>
          {insurancePriceLoading ? (
            <Skeleton className="h-4 w-16" />
          ) : (
            <p className="text-sm font-medium text-gray-light-900">{formatPrice(insurancePrice)}</p>
          )}
        </div>
      )}

      {/* Promo code slot (checkout only) */}
      {promoCodeSlot}

      <div className="h-px w-full bg-gray-light-200" />

      {/* Total */}
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between">
          <span className="text-lg font-semibold text-gray-light-900">Total</span>
          {insurancePriceLoading || (!priceView && flightPrice === 0) ? (
            <Skeleton className="h-6 w-24" />
          ) : (
            <span className="text-lg font-semibold text-gray-light-900">
              {formatPrice(effectiveTotal)}
            </span>
          )}
        </div>
        <p className="text-xs font-medium text-gray-light-600">Taxes et frais compris</p>
      </div>

      {ticketConditionsCard}

      {/* Journey cards */}
      {priceView && priceView.journeys.length > 0 && (
        <div className="mt-2 flex flex-col gap-2">
          {priceView.journeys.map((journey, i) => {
            const firstSegment = journey.segments[0]
            const lastSegment = journey.segments.at(-1)
            if (!firstSegment || !lastSegment) return null

            const isMultiDestination = priceView.journeys.length > 2
            const journeyLabel =
              isMultiDestination || journey.type === 'segment'
                ? `Vol ${i + 1}`
                : JOURNEY_TYPE_LABELS[journey.type]

            const route = `${firstSegment.departure.airport} vers ${lastSegment.arrival.airport}`
            return (
              <FlightSegmentCard
                key={i}
                airline={journey.carrier.name}
                airlineLogoUrl={journey.carrier.logoUrl}
                route={route}
                departureLabel={`${firstSegment.departure.date} à ${firstSegment.departure.time}`}
                arrivalLabel={`${lastSegment.arrival.date} à ${lastSegment.arrival.time}`}
                journeyTypeLabel={journeyLabel}
                stopsLabel=<StopsIndicator
                  legs={journey.legs}
                  stops={journey.stops}
                  variant="compact"
                />
                durationLabel={formatDuration(journey.totalDuration, 'compact')}
              />
            )
          })}
        </div>
      )}

      {/* Contact info (checkout only) */}
      {contactInfo && (
        <SidebarCard title="Information du contact">
          <div className="flex flex-col gap-2 text-sm font-medium text-gray-light-700">
            {contactInfo.phone && <p>{contactInfo.phone}</p>}
            <p>{contactInfo.email}</p>
          </div>
        </SidebarCard>
      )}

      {/* Passenger details */}
      {passengers.length > 0 && (
        <SidebarCard title="Détails des voyageurs">
          {passengers.map((passenger, index) => (
            <PassengerCard
              key={`passenger-${index}`}
              firstName={passenger.firstName}
              lastName={passenger.lastName}
              type={passenger.type}
              gender={passenger.gender}
              birthDate={passenger.birthDate}
            />
          ))}
        </SidebarCard>
      )}

      {/* Luggage */}
      {priceView?.luggage && (
        <SidebarCard title="Bagages inclus">
          <div className="flex flex-col gap-4">
            <div className="flex items-start gap-5">
              <Luggage className="size-6 shrink-0 text-gray-light-600" />
              <div className="flex flex-col gap-2">
                <p className="text-sm font-medium text-gray-light-700">
                  {priceView.luggage.checkedBags.included
                    ? `${priceView.luggage.checkedBags.quantity} bagage${priceView.luggage.checkedBags.quantity > 1 ? 's' : ''} soute`
                    : 'Aucun bagage en soute inclus'}
                </p>
                {priceView.luggage.checkedBags.included &&
                  priceView.luggage.checkedBags.weightKgPerBag !== null &&
                  priceView.luggage.checkedBags.weightKgPerBag !== undefined && (
                    <p className="text-xs font-medium text-gray-light-600">
                      Poids maximum : {priceView.luggage.checkedBags.weightKgPerBag} kg chacun
                    </p>
                  )}
              </div>
            </div>
            <div className="flex items-start gap-5">
              <Briefcase className="size-6 shrink-0 text-gray-light-600" />
              <div className="flex flex-col gap-2">
                <p className="text-sm font-medium text-gray-light-700">
                  {priceView.luggage.cabinBag.included
                    ? `${priceView.luggage.cabinBag.quantity ?? 1} bagage${(priceView.luggage.cabinBag.quantity ?? 1) > 1 ? 's' : ''} cabine`
                    : 'Pas de bagage cabine'}
                </p>
                {priceView.luggage.cabinBag.included &&
                  priceView.luggage.cabinBag.weightKg !== null &&
                  priceView.luggage.cabinBag.weightKg !== undefined && (
                    <p className="text-xs font-medium text-gray-light-600">
                      Poids maximum : {priceView.luggage.cabinBag.weightKg} kg chacun
                    </p>
                  )}
              </div>
            </div>
          </div>
        </SidebarCard>
      )}

      {/* Moyens de paiement */}
      <details className="group mt-1 border-t border-gray-light-200 pt-4" open={!isMobile}>
        <summary className="flex cursor-pointer list-none items-center justify-between text-sm font-semibold text-gray-light-900 [&::-webkit-details-marker]:hidden">
          Moyens de paiement acceptés
          <ChevronDown className="size-4 text-gray-light-600 transition-transform group-open:rotate-180" />
        </summary>
        <ul className="mt-3 grid grid-cols-2 gap-x-4 gap-y-3">
          {paymentMethods.map(({ Icon, label }) => (
            <li
              className="flex items-center gap-2 text-xs font-medium text-gray-light-700"
              key={label}
            >
              <Icon className="size-4 shrink-0 text-gray-light-600" />
              {label}
            </li>
          ))}
        </ul>
      </details>
    </div>
  )
}

// ── Sub-components ──

/**
 * Section du panneau tarif : titre + séparateur, sans carte imbriquée — le panneau
 * extérieur porte déjà la seule bordure du bloc.
 */
function SidebarCard({ children, title }: { children: ReactNode; title: string }) {
  return (
    <div className="mt-1 flex flex-col gap-3 border-t border-gray-light-200 pt-4">
      <p className="text-sm font-semibold text-gray-light-900">{title}</p>
      {children}
    </div>
  )
}
